import { Injectable } from '@angular/core';
import { ModifyARRequest } from '@aiwyn/gql-api/src/models/request/modifyar-request.model';
import { Globals } from '@anduin/config';
import { Logger, isEmpty, removeDuplicates, removeDuplicatesByField } from '@anduin/shared';
import { Apollo, gql } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  AbstractArTran,
  AllocationStatus,
  ArTranBaseType,
  ArTranEntity,
  ArTranFragment,
  BaseArTranLabels,
  CreateArTranPayload,
  CreatePaymentPayload,
  CreditArTranType,
  DebitArTranType,
  InferredArTranLink,
  ModifyArTranPayload,
  UnallocatedArTranIdsByClient,
} from '../models';

export interface GraphQlQueryOptions {
  subWhereClause?: string; // only get for certain client, only get certain allocation status,
  orderBy?: string; // get oldest first
  limit?: number; // search results limit
}

@Injectable({
  providedIn: 'root',
})
export class ArTranApiService {
  constructor(
    private readonly apollo: Apollo,
    private readonly logger: Logger,
    private readonly globals: Globals,
  ) {}

  getArTransById(arTranIds: number): Observable<ArTranEntity>;
  getArTransById(arTranIds: number[]): Observable<ArTranEntity[]>;
  getArTransById(arTranIds: number | number[]): Observable<ArTranEntity | ArTranEntity[]> {
    let queryName = 'GetArTransById',
      shouldReturnArray = true;
    if (typeof arTranIds === 'number') {
      arTranIds = [arTranIds];
      queryName = 'GetArTranById';
      shouldReturnArray = false;
    }

    const arTranFragment = new ArTranFragment();

    return this.apollo
      .query<{ arTrans: any[] }>({
        query: gql`
        query ${queryName} {
          arTrans: ar_tran (
            where: { id: { _in: [${arTranIds}] } }
          ) {
            ...${arTranFragment.getName()}
          }
        }
        ${arTranFragment.getDependencyFragments()}
      `,
      })
      .pipe(
        map(({ data }) => {
          const results = data.arTrans.map((arTran) => arTranFragment.map(arTran));
          return shouldReturnArray ? results : results[0];
        }),
      );
  }

  getLinkedArTrans(arTranIds: number[]): Observable<ArTranEntity[]>;
  getLinkedArTrans(arTranIds: number, coreArTranBaseType: ArTranBaseType): Observable<ArTranEntity[]>;
  getLinkedArTrans(arTranIds: number | number[], coreArTranBaseType?: ArTranBaseType): Observable<ArTranEntity[]> {
    if (arTranIds == undefined || (typeof arTranIds === 'number' && coreArTranBaseType == undefined)) {
      this.logger.error(`getLinkedArTrans called with invalid params: [${arTranIds}, ${coreArTranBaseType}]`);
      return of([]);
    }

    if (Array.isArray(arTranIds)) {
      return this.getArTransById(arTranIds);
    }

    const arTranFragment = new ArTranFragment();

    let coreArTranIdField: string, targetArTranRelationshipField: string;
    if (coreArTranBaseType === ArTranBaseType.DEBIT) {
      coreArTranIdField = 'ar_tran_base_id';
      targetArTranRelationshipField = 'ar_tran_link_mod_to_ar_tran';
    } else {
      coreArTranIdField = 'ar_tran_mod_id';
      targetArTranRelationshipField = 'ar_tran_link_base_to_ar_tran';
    }

    return this.apollo
      .query<{ arTranLinks: { linkedArTran: any }[] }>({
        query: gql`
        query GetLinkedArTrans {
          arTranLinks: ar_tran_link (
            where: {
              active: { _eq: true },
              ${coreArTranIdField}: { _eq: ${arTranIds} }
            }
          ) {
            linkedArTran: ${targetArTranRelationshipField} {
              ...${arTranFragment.getName()}
            }
          }
        }
        ${arTranFragment.getDependencyFragments()}
      `,
      })
      .pipe(
        map(({ data }) =>
          removeDuplicatesByField(
            data.arTranLinks.map((link) => arTranFragment.map(link.linkedArTran)),
            'id',
          ),
        ),
      );
  }

  /**
   * Check if the AR transaction matches the context provided by the where clause.
   * Useful for validating a transaction selected via manually typed-out URL query params.
   * No, Patrick. Transaction #1337 does not belong to client #42.
   *
   * @param arTranId
   * @param whereClause
   */
  doesArTranMatchContext(arTranId: number, whereClause: string): Observable<boolean> {
    return this.apollo
      .query<{ arTrans: { id: number }[] }>({
        query: gql`
        query DoesArTranMatchContext {
          arTrans: ar_tran (
            where: {
              id: { _eq: ${arTranId} },
              ${whereClause}
            }
          ) {
            id
          }
        }
      `,
      })
      .pipe(map(({ data }) => data.arTrans[0] != undefined));
  }

  getUnallocatedArTranIdsByClient(): Observable<UnallocatedArTranIdsByClient> {
    return this.apollo
      .query<{ arTrans: { id: number; clientId: number }[] }>({
        query: gql`
        query GetUnallocatedArTranIdsByClient {
          arTrans: vw_ar_tran (
            where: {
              isActive: { _eq: true },
              allocationStatus: { _neq: "${AllocationStatus.FULL}" }
            }
          ) {
            id: arTranId
            clientId
          }
        }
      `,
      })
      .pipe(
        map(({ data }) => {
          const uniqueClientIds = removeDuplicates(data.arTrans.map((arTran) => arTran.clientId));
          return uniqueClientIds.reduce((acc, uniqueClientId) => {
            acc[uniqueClientId] = data.arTrans.filter(({ clientId }) => clientId === uniqueClientId).map(({ id }) => id);
            return acc;
          }, {} as UnallocatedArTranIdsByClient);
        }),
      );
  }

  private _modifyTransaction(modifyArTranPayload: ModifyArTranPayload, modificationName: string): Observable<number> {
    return this.apollo
      .mutate<{ id: number }>({
        mutation: gql`
        mutation ${modificationName} ($modifyARRequest: ModifyARRequest!) {
          ModifyAR (modifyARRequest: $modifyARRequest) {
            id
          }
        }
      `,
        variables: {
          modifyARRequest: {
            ...modifyArTranPayload,
            tenant: { schema: this.globals.getTenant() },
          },
        },
      })
      .pipe(
        map(({ data }) => data.id),
        tap(
          (_) => {},
          (error) => this.logger.error(`ArTranApiService :: ${modificationName} :: Error: ${error}`),
        ),
      );
  }

  /**
   * Generic method to update any transaction fields using ModifyARRequest
   * @param arTranId - The ID of the transaction to update
   * @param updateData - Partial ModifyARRequest data (excluding id, tenant, and allocations)
   * @returns Observable<number> - The updated transaction ID
   */
  updateTransaction(arTranId: number, updateData: Partial<Omit<ModifyARRequest, 'id' | 'tenant' | 'allocations'>>): Observable<number> {
    const modifyARRequest: ModifyARRequest = {
      id: arTranId,
      allocations: [],
      tenant: { schema: this.globals.getTenant() },
      ...updateData,
    };

    return this.apollo
      .mutate<{ id: number }>({
        mutation: gql`
          mutation UpdateTransaction($modifyARRequest: ModifyARRequest!) {
            ModifyAR(modifyARRequest: $modifyARRequest) {
              id
            }
          }
        `,
        variables: {
          modifyARRequest,
        },
      })
      .pipe(
        map(({ data }) => data.id),
        tap(
          (_) => {},
          (error) => this.logger.error(`ArTranApiService :: updateTransaction :: Error: ${error}`),
        ),
      );
  }

  saveTransactionNote(arTranId: number, note: string): Observable<number> {
    const modifyArTranPayload: ModifyArTranPayload = {
      id: arTranId,
      notes: note,
    };

    return this._modifyTransaction(modifyArTranPayload, 'SaveTransactionNote');
  }

  saveTransactionTotal(arTranId: number, total: number): Observable<number> {
    const modifyArTranPayload: ModifyArTranPayload = {
      id: arTranId,
      amount: total,
    };

    return this._modifyTransaction(modifyArTranPayload, 'SaveTransactionTotal');
  }

  saveTransactionLinks(targetArTran: AbstractArTran, newTransactionLinks: InferredArTranLink[]): Observable<number> {
    const modifyArTranPayload: ModifyArTranPayload = {
      id: targetArTran.id,
      allocations: newTransactionLinks,
    };

    return this._modifyTransaction(modifyArTranPayload, 'SaveTransactionLinks');
  }

  savePaymentTransactionLinks(targetArTran: AbstractArTran, newTransactionLinks: InferredArTranLink[]): Observable<number> {
    const newArTranLinks = newTransactionLinks.map((link) => ({
      arTranId: link.arTranId,
      modARTranId: targetArTran.id,
      allocated: link.allocated,
    }));

    return this.apollo
      .mutate<{ id: number }>({
        mutation: gql`
          mutation SavePaymentTransactionsLinks($applyARRequest: ApplyARRequest!) {
            ApplyAR(applyARRequest: $applyARRequest) {
              id
            }
          }
        `,
        variables: {
          applyARRequest: {
            allocations: newArTranLinks,
            tenant: { schema: this.globals.getTenant() },
          },
        },
      })
      .pipe(
        map(({ data }) => data.id),
        tap(
          (_) => {},
          (error) => this.logger.error(`ArTranApiService :: savePaymentTransactionLinks :: Error: ${error}`),
        ),
      );
  }

  getLinkableArTrans(
    clientId: number,
    unallocatedArTranIds: number[],
    targetArTranTypes: CreditArTranType[] | DebitArTranType[],
  ): Observable<ArTranEntity[]> {
    if (clientId == undefined) {
      this.logger.error('ArTranApiService :: getLinkableArTrans :: clientId is required.');
      return of([]);
    }

    if (isEmpty(unallocatedArTranIds)) {
      this.logger.info('ArTranApiService :: getLinkableArTrans :: No unallocated transactions available for new allocations.');
      return of([]);
    }

    const targetArTranTypesClause = this._buildTargetArTranTypesClause(targetArTranTypes);
    const arTranFragment = new ArTranFragment();

    return this.apollo
      .query<{ arTrans: any[] }>({
        query: gql`
        query GetDefaultArTranLinkOptions {
          arTrans: ar_tran (
            where: {
              id: { _in: [${unallocatedArTranIds}] },
              ${targetArTranTypesClause}
              clientId: { _eq: ${clientId} }
            },
            order_by: { transactionDate: asc },
          ) {
            ...${arTranFragment.getName()}
          }
        }
        ${arTranFragment.getDependencyFragments()}
      `,
      })
      .pipe(map(({ data }) => data.arTrans.map((arTran) => arTranFragment.map(arTran))));
  }

  getLinkableArTransForClient(clientId: number, targetArTranTypes: CreditArTranType[] | DebitArTranType[]): Observable<ArTranEntity[]> {
    if (clientId == undefined) {
      this.logger.error('ArTranApiService :: getLinkableArTransForClient :: clientId is required.');
      return of([]);
    }

    const targetArTranTypesClause = this._buildTargetArTranTypesClause(targetArTranTypes, ['Payment']);

    return this.apollo
      .query<{ arTrans: { id: number }[] }>({
        query: gql`
        query GetUnallocatedArTransViewByClient($clientId: Int!) {
          arTrans: vw_ar_tran (
            where: {
              isActive: { _eq: true },
              allocationStatus: { _neq: "${AllocationStatus.FULL}" },
              clientId: { _eq: $clientId },
              ${targetArTranTypesClause}
            }
          ) {
            id: arTranId
          }
        }
      `,
        variables: { clientId },
      })
      .pipe(
        map(({ data }) => data.arTrans.map(({ id }) => id)),
        switchMap((ids) => this.getLinkableArTrans(clientId, ids, targetArTranTypes)),
      );
  }

  getArTransBySearchTerm(searchTerm: string): Observable<ArTranEntity[]>;
  getArTransBySearchTerm(
    searchTerm: string,
    targetArTranIds: number[],
    targetArTranTypes: DebitArTranType[] | CreditArTranType[],
  ): Observable<ArTranEntity[]>;
  getArTransBySearchTerm(
    searchTerm: string,
    targetArTranIds?: number[],
    targetArTranTypes?: DebitArTranType[] | CreditArTranType[],
  ): Observable<ArTranEntity[]> {
    if (!searchTerm) {
      return of([]);
    }

    const arTranFragment = new ArTranFragment();
    const ilikeSearchTerm = `{ _ilike: "%${searchTerm}%" }`;
    const targetArTranIdsClause = isEmpty(targetArTranIds) ? '' : `id: { _in: [${targetArTranIds}] },`;
    const targetArTranTypesClause = isEmpty(targetArTranTypes) ? '' : `type: { _in: [${targetArTranTypes}] },`;
    const numericSearchClause = isNaN(Number(searchTerm))
      ? ''
      : `
      { id: { _eq: ${searchTerm} } },
      { amount: { _eq: ${searchTerm} } },
      { allocated: { _eq: ${searchTerm} } },
    `;

    return this.apollo
      .query<{ arTrans: any[] }>({
        query: gql`
        query GetArTransBySearchTerm {
          arTrans: ar_tran (
            where: {
              ${targetArTranIdsClause}
              ${targetArTranTypesClause}
              _or: [
                ${numericSearchClause}
                { type: ${ilikeSearchTerm} },
                { notes: ${ilikeSearchTerm} },
                { invoice: { invoiceNumber: ${ilikeSearchTerm} } },
                { client: { _or: [{ name: ${ilikeSearchTerm} }, { clientRef: ${ilikeSearchTerm} }] } }
                { managerStaff: { fullName: ${ilikeSearchTerm} } },
                { partnerStaff: { fullName: ${ilikeSearchTerm} } },
              ]
            },
            order_by: { transactionDate: asc },
            limit: 20
          ) {
            ...${arTranFragment.getName()}
          }
        }
        ${arTranFragment.getDependencyFragments()}
      `,
      })
      .pipe(map(({ data }) => data.arTrans.map((arTran) => arTranFragment.map(arTran))));
  }

  getArTranLabels(): Observable<BaseArTranLabels> {
    return this.apollo
      .query<BaseArTranLabels>({
        query: gql`
        query GetBaseArTranLabels {
          ${this._getArTranLabelQuery('credit')}
          ${this._getArTranLabelQuery('debit')}
        }
      `,
      })
      .pipe(map(({ data }) => data));
  }

  private _buildTargetArTranTypesClause(targetArTranTypes: CreditArTranType[] | DebitArTranType[], additionalTypes: string[] = []): string {
    const allTypes = [...targetArTranTypes, ...additionalTypes];
    return isEmpty(allTypes) ? '' : `type: { _in: [${allTypes}] },`;
  }

  private _getArTranLabelQuery(type: 'credit' | 'debit'): string {
    return `
      ${type}: ${type}_label (
        where: { is_active: { _eq: true } },
        order_by: { name: asc },
      ) {
        id: ${type}_label_id
        name
      }
    `;
  }

  createArTran(arTran: CreateArTranPayload): Observable<number> {
    if (arTran.arType === CreditArTranType.RECEIPT) {
      return this.createManualPayment(arTran as CreatePaymentPayload);
    }

    return this.apollo
      .mutate<{ id: number }>({
        mutation: gql`
          mutation CreateAR($createARRequest: CreateARRequest!) {
            CreateAR(createARRequest: $createARRequest) {
              id
            }
          }
        `,
        variables: {
          createARRequest: {
            ...arTran,
            tenant: { schema: this.globals.getTenant() },
          },
        },
      })
      .pipe(map(({ data }) => data.id));
  }

  createManualPayment(arTran: CreatePaymentPayload): Observable<number> {
    delete arTran.arType;

    return this.apollo
      .mutate<{ id: number }>({
        mutation: gql`
          mutation ManualPayment($manualPaymentRequest: ManualPaymentRequest!) {
            ManualPayment(manualPaymentRequest: $manualPaymentRequest) {
              id
            }
          }
        `,
        variables: {
          manualPaymentRequest: { ...arTran } as CreatePaymentPayload,
        },
      })
      .pipe(map(({ data }) => data.id));
  }

  deactivateTransaction(arTranId: number): Observable<number> {
    return this.apollo
      .mutate<{ id: number }>({
        mutation: gql`
          mutation ModifyARRequest($reverseARRequest: ReverseARRequest!) {
            ReverseAR(reverseARRequest: $reverseARRequest) {
              id
            }
          }
        `,
        variables: {
          reverseARRequest: {
            id: arTranId,
            tenant: { schema: this.globals.getTenant() },
          },
        },
      })
      .pipe(map(({ data }) => data.id));
  }

  setPaymentAsNsf(arTranId: number): Observable<number> {
    // return throwError('This is a test error');

    return this.apollo
      .mutate<{ id: number }>({
        mutation: gql`
          mutation FailManualPayment($failManualPaymentRequest: FailManualPaymentRequest!) {
            FailManualPayment(failManualPaymentRequest: $failManualPaymentRequest) {
              id
            }
          }
        `,
        variables: {
          failManualPaymentRequest: {
            id: arTranId,
            failureReason: 'Non-sufficient Funds',
          },
        },
      })
      .pipe(map(({ data }) => data.id));
  }
}
